/** The one query client the app runs on, and the three defaults that make a read honest.
 *
 * - **No retries.** A failed request should surface as an error the reader can act on, not as a long
 *   hang (the read states in `components/Loaded` are built for exactly that).
 * - **No refetch on focus, and always stale.** A figure on screen must not be a guess from cache.
 * - **`networkMode: "always"`** (E2E P9, G-P9-23). TanStack Query's default, `"online"`, *pauses* a
 *   query while the browser reports itself offline: it stays `pending` and never fails. A screen
 *   that opens offline then shows its loading state for ever, or - where a tile prints a dash for a
 *   read that has not answered - a row of dashes that look like figures nobody has. The request is
 *   made anyway; `fetch` rejects with a `TypeError`, the query *fails*, and every screen's own failed
 *   state (`ReadFailed` and its Try again) says so, which is the one honest answer to "the network
 *   is gone". Mutations get the same: a save that is paused offline is a button that says "Saving…"
 *   for ever, and the write's own refusal copy already knows what an unknown outcome is
 *   (`WriteFailed`, `isAmbiguous`).
 */

import { QueryClient } from "@tanstack/react-query";

export const QUERY_DEFAULTS = {
  refetchOnWindowFocus: false,
  retry: 0,
  staleTime: 0,
  networkMode: "always",
} as const;

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: { ...QUERY_DEFAULTS },
      mutations: { networkMode: "always" },
    },
  });
}
